/**
 * exportCalendarToExcel.js
 *
 * Drop this file anywhere in your project (e.g. src/utils/) and import
 * `exportCalendarToExcel` in ManageAttendance.jsx.
 *
 * Requires: xlsx  (SheetJS)   →  npm install xlsx
 */

import * as XLSX from "xlsx";

// ─── colour palette (matches the React calendar exactly) ────────────────────
const COLOR = {
  present: { bg: "F0FDF4", text: "16A34A" },
  partial: { bg: "FFFBEB", text: "D97706" },
  absent: { bg: "FEF2F2", text: "DC2626" },
  weeklyOff: { bg: "F1F5F9", text: "64748B" },
  companyHoliday: { bg: "FEF3C7", text: "92400E" },
  leave: { bg: "FFEDD5", text: "9A3412" },
  halfLeave: { bg: "FEF9C3", text: "854D0E" },
  future: { bg: "FFFFFF", text: "94A3B8" },
  // stat header colours
  hdrPresent: "10B981",
  hdrPartial: "F59E0B",
  hdrAbsent: "EF4444",
  hdrWeeklyOff: "94A3B8",
  hdrHoliday: "FBBF24",
  hdrLeave: "FB923C",
  hdrHours: "8B5CF6",
  hdrAvg: "06B6D4",
  // stat cell colours (light tint)
  cellPresent: "ECFDF5",
  cellPartial: "FEF3C7",
  cellAbsent: "FEE2E2",
  cellWeeklyOff: "F1F5F9",
  cellHoliday: "FEF9C3",
  cellLeave: "FFEDD5",
  cellHours: "F3E8FF",
  cellAvg: "ECFEFF",
  // totals row
  totalBg: "FEF9C3",
  totalText: "854D0E",
  // employee name col
  empHeader: "F3F4F6",
  empCell: "F9FAFB",
  // day header
  dayHeader: "F9FAFB",
};

// ─── helpers ────────────────────────────────────────────────────────────────

const isFullDayLeave = (d) => {
  if (!d) return false;
  const u = d.trim().toUpperCase();
  return u === "FULL_DAY" || u === "FULL DAY";
};

const isHoliday = (date, workingDays) => {
  if (!workingDays) return false;
  const dayName = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ][date.getDay()];
  if (!workingDays[dayName]) return true;
  const weekOfMonth = Math.ceil(date.getDate() / 7);
  const weekName = ["first", "second", "third", "fourth", "fifth"][
    weekOfMonth - 1
  ];
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  if (dayName === "saturday" && workingDays[`leaveOn${cap(weekName)}Saturday`])
    return true;
  if (dayName === "sunday" && workingDays[`leaveOn${cap(weekName)}Sunday`])
    return true;
  return false;
};

const calcHours = (punchIn, punchOut) => {
  if (!punchIn || !punchOut) return 0;
  const parse = (t) => {
    if (typeof t === "object" && t.hour !== undefined)
      return t.hour + t.minute / 60;
    const m = String(t).match(/^(\d{1,2}):(\d{2})/);
    if (m) return parseInt(m[1]) + parseInt(m[2]) / 60;
    return 0;
  };
  let h = parse(punchOut) - parse(punchIn);
  if (h < 0) h += 24;
  return Math.max(0, h);
};

const fmt12 = (t) => {
  if (!t) return "";
  let h, m;
  if (typeof t === "object" && t.hour !== undefined) {
    h = t.hour;
    m = t.minute;
  } else {
    const match = String(t).match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)?/i);
    if (!match) return String(t);
    h = parseInt(match[1]);
    m = parseInt(match[2]);
    if (match[4]?.toLowerCase() === "pm" && h !== 12) h += 12;
    if (match[4]?.toLowerCase() === "am" && h === 12) h = 0;
  }
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
};

// Build per-employee stats identical to AttendanceCalendar.jsx
const calcEmployeeStats = (
  employee,
  year,
  month,
  daysInMonth,
  companyHolidays,
) => {
  const attMap = {};
  const leaveMap = {};
  (employee.monthlyAttendance || []).forEach((a) => {
    attMap[a.attendanceDate] = a;
  });
  (employee.leaveHistories || []).forEach((l) => {
    leaveMap[l.leaveDate] = l;
  });

  const findHoliday = (dateStr) =>
    (companyHolidays || []).find((h) => h.eventDate === dateStr);

  const today = new Date();
  const isCurrentMonth =
    today.getFullYear() === year && today.getMonth() === month;
  const daysToCount = isCurrentMonth ? today.getDate() : daysInMonth;

  let present = 0,
    partial = 0,
    absent = 0,
    holidays = 0,
    leaves = 0,
    compHols = 0,
    totalHours = 0;

  for (let day = 1; day <= daysToCount; day++) {
    const dateKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const date = new Date(year, month, day);
    const todayMid = new Date(today);
    todayMid.setHours(0, 0, 0, 0);
    const dateMid = new Date(date);
    dateMid.setHours(0, 0, 0, 0);
    if (dateMid > todayMid) continue;

    const record = attMap[dateKey];
    const leave = leaveMap[dateKey];
    const ch = findHoliday(dateKey);
    const isOpt = ch && ch.holidayType === "Optional";

    if (ch && !(isOpt && leave)) {
      compHols++;
      continue;
    }
    if (isHoliday(date, employee.workingDays)) {
      holidays++;
      continue;
    }
    if (leave && isFullDayLeave(leave.leaveDuration)) {
      leaves++;
      continue;
    }

    if (record?.punchInTime && record?.punchOutTime) {
      present++;
      totalHours += calcHours(record.punchInTime, record.punchOutTime);
    } else if (record?.punchInTime) {
      partial++;
    } else {
      absent++;
    }
  }

  const avgHours = present > 0 ? totalHours / present : 0;
  return {
    present,
    partial,
    absent,
    holidays,
    leaves,
    compHols,
    totalHours,
    avgHours,
  };
};

// Determine cell status + colour for a given day
const getCellInfo = (
  record,
  leave,
  isHol,
  companyHoliday,
  isOptHoliday,
  dateKey,
  year,
  month,
) => {
  if (companyHoliday && !(isOptHoliday && leave)) {
    return {
      status: companyHoliday.holidayName || "Holiday",
      color: COLOR.companyHoliday,
    };
  }
  if (isHol) return { status: "Weekly Off", color: COLOR.weeklyOff };
  if (leave) {
    const full = isFullDayLeave(leave.leaveDuration);
    return {
      status: full ? "LEAVE" : "½ DAY",
      color: full ? COLOR.leave : COLOR.halfLeave,
    };
  }

  const cellDate = new Date(dateKey);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  cellDate.setHours(0, 0, 0, 0);
  if (cellDate > today) return { status: "", color: COLOR.future };

  if (!record) return { status: "Absent", color: COLOR.absent };
  if (record.punchInTime && record.punchOutTime)
    return { status: "Present", color: COLOR.present };
  if (record.punchInTime) return { status: "Partial", color: COLOR.partial };
  return { status: "Absent", color: COLOR.absent };
};

// openpyxl-style cell style helper for SheetJS cell_styles extension
// SheetJS free tier uses `s` property on each cell
const makeStyle = (
  bgHex,
  textHex,
  { bold = false, center = false, wrapText = false, border = false } = {},
) => {
  const s = {
    font: { name: "Arial", sz: 10, bold, color: { rgb: textHex || "000000" } },
    fill: { patternType: "solid", fgColor: { rgb: bgHex || "FFFFFF" } },
    alignment: {
      horizontal: center ? "center" : "left",
      vertical: "center",
      wrapText,
    },
  };
  if (border) {
    const b = { style: "thin", color: { rgb: "D1D5DB" } };
    s.border = { top: b, bottom: b, left: b, right: b };
  }
  return s;
};

// ─── main export function ────────────────────────────────────────────────────

/**
 * @param {Object[]} employeeList   - from ManageAttendance state
 * @param {Object}   calendarData   - keyed by employeeName → { dateKey: record }
 * @param {Date}     currentMonth
 * @param {string}   monthLabel     - e.g. "June 2026"
 */
export function exportCalendarToExcel(
  employeeList,
  calendarData,
  currentMonth,
  monthLabel,
) {
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Gather company holidays from first employee with data
  const companyHolidays = employeeList[0]?.holidays || [];
  const findHoliday = (dateStr) =>
    companyHolidays.find((h) => h.eventDate === dateStr);

  const wb = XLSX.utils.book_new();
  const ws = {};

  // ── column layout ──────────────────────────────────────────────────────────
  // Col 0 : Employee name
  // Col 1-8: Present | Partial | Absent | Weekly Offs | Holidays | Leaves | Total Hours | Avg Hrs/Day
  // Col 9+: day 1 … day N
  const STAT_COLS = 8;
  const DATA_START_COL = 1 + STAT_COLS; // col index of day-1

  // helper: col index → Excel letter
  const colLetter = (i) => {
    let s = "";
    i++; // 1-based
    while (i > 0) {
      s = String.fromCharCode(65 + ((i - 1) % 26)) + s;
      i = Math.floor((i - 1) / 26);
    }
    return s;
  };

  const cell = (r, c, value, style) => {
    const ref = `${colLetter(c)}${r + 1}`;
    ws[ref] = { v: value, t: typeof value === "number" ? "n" : "s", s: style };
  };

  // ── Row 0: Title ───────────────────────────────────────────────────────────
  cell(
    0,
    0,
    `Attendance Calendar — ${monthLabel}`,
    makeStyle("1E3A5F", "FFFFFF", { bold: true, center: false }),
  );
  // merge across all columns (done via !merges below)

  // ── Row 1: Column headers ──────────────────────────────────────────────────
  const HDR_ROW = 1;
  cell(
    HDR_ROW,
    0,
    "Employee",
    makeStyle(COLOR.empHeader, "374151", {
      bold: true,
      center: false,
      border: true,
    }),
  );

  const statHeaders = [
    { label: "Present", bg: COLOR.hdrPresent },
    { label: "Partial", bg: COLOR.hdrPartial },
    { label: "Absent", bg: COLOR.hdrAbsent },
    { label: "Weekly Offs", bg: COLOR.hdrWeeklyOff },
    { label: "Holidays", bg: COLOR.hdrHoliday },
    { label: "Leaves", bg: COLOR.hdrLeave },
    { label: "Total Hours", bg: COLOR.hdrHours },
    { label: "Avg Hrs/Day", bg: COLOR.hdrAvg },
  ];
  statHeaders.forEach(({ label, bg }, i) => {
    cell(
      HDR_ROW,
      1 + i,
      label,
      makeStyle(bg, "FFFFFF", { bold: true, center: true, border: true }),
    );
  });

  days.forEach((d, i) => {
    const dateObj = new Date(year, month, d);
    const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
    cell(
      HDR_ROW,
      DATA_START_COL + i,
      `${d}\n${dayNames[dateObj.getDay()]}`,
      makeStyle(COLOR.dayHeader, "374151", {
        bold: true,
        center: true,
        wrapText: true,
        border: true,
      }),
    );
  });

  // ── Data rows ──────────────────────────────────────────────────────────────
  let totalPresent = 0,
    totalPartial = 0,
    totalAbsent = 0;
  let totalHols = 0,
    totalCompHols = 0,
    totalLeaves = 0;
  let totalHours = 0,
    totalDays = 0;

  employeeList.forEach((employee, rowIdx) => {
    const ROW = HDR_ROW + 1 + rowIdx;
    const employeeName = employee.employeeName;
    const empData = calendarData[employeeName] || {};

    const stats = calcEmployeeStats(
      employee,
      year,
      month,
      daysInMonth,
      companyHolidays,
    );
    totalPresent += stats.present;
    totalPartial += stats.partial;
    totalAbsent += stats.absent;
    totalHols += stats.holidays;
    totalCompHols += stats.compHols;
    totalLeaves += stats.leaves;
    totalHours += stats.totalHours;
    if (stats.present > 0) totalDays += stats.present;

    // employee name
    cell(
      ROW,
      0,
      employeeName,
      makeStyle(COLOR.empCell, "1F2937", { bold: true, border: true }),
    );

    // stat cells
    const statValues = [
      { v: stats.present, bg: COLOR.cellPresent, fg: "065F46" },
      { v: stats.partial, bg: COLOR.cellPartial, fg: "92400E" },
      { v: stats.absent, bg: COLOR.cellAbsent, fg: "991B1B" },
      { v: stats.holidays, bg: COLOR.cellWeeklyOff, fg: "475569" },
      { v: stats.compHols, bg: COLOR.cellHoliday, fg: "854D0E" },
      { v: stats.leaves, bg: COLOR.cellLeave, fg: "9A3412" },
      {
        v: `${Math.round(stats.totalHours)}h`,
        bg: COLOR.cellHours,
        fg: "6B21A8",
      },
      { v: `${stats.avgHours.toFixed(1)}h`, bg: COLOR.cellAvg, fg: "155E75" },
    ];
    statValues.forEach(({ v, bg, fg }, i) => {
      cell(ROW, 1 + i, v, makeStyle(bg, fg, { center: true, border: true }));
    });

    // day cells
    days.forEach((d, i) => {
      const dateKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const date = new Date(year, month, d);
      const record = empData[dateKey];
      const leave = (employee.leaveHistories || []).find(
        (l) => l.leaveDate === dateKey,
      );
      const ch = findHoliday(dateKey);
      const isOpt = ch && ch.holidayType === "Optional";
      const isHol = isHoliday(date, employee.workingDays);
      const { status, color } = getCellInfo(
        record,
        leave,
        isHol,
        ch,
        isOpt && leave,
        dateKey,
        year,
        month,
      );

      let displayText = status;
      if (record?.punchInTime) displayText += `\n${fmt12(record.punchInTime)}`;
      if (record?.punchOutTime)
        displayText += `\n${fmt12(record.punchOutTime)}`;

      cell(
        ROW,
        DATA_START_COL + i,
        displayText,
        makeStyle(color.bg, color.text, {
          center: true,
          wrapText: true,
          border: true,
        }),
      );
    });
  });

  // ── Totals row ─────────────────────────────────────────────────────────────
  const TOTAL_ROW = HDR_ROW + 1 + employeeList.length;
  const totalStyle = makeStyle(COLOR.totalBg, COLOR.totalText, {
    bold: true,
    center: true,
    border: true,
  });
  cell(
    TOTAL_ROW,
    0,
    "TOTAL",
    makeStyle(COLOR.totalBg, COLOR.totalText, { bold: true, border: true }),
  );
  [
    totalPresent,
    totalPartial,
    totalAbsent,
    totalHols,
    totalCompHols,
    totalLeaves,
    `${Math.round(totalHours)}h`,
    `${totalDays > 0 ? (totalHours / totalDays).toFixed(1) : "0.0"}h`,
  ].forEach((v, i) => cell(TOTAL_ROW, 1 + i, v, totalStyle));

  days.forEach((_, i) => {
    cell(
      TOTAL_ROW,
      DATA_START_COL + i,
      "",
      makeStyle(COLOR.totalBg, COLOR.totalText, { border: true }),
    );
  });

  // ── Legend sheet ────────────────────────────────────────────────────────────
  const legendWs = {};
  const legendData = [
    ["Status", "Colour", "Meaning"],
    ["Present", "#F0FDF4", "Employee punched in and out"],
    ["Partial", "#FFFBEB", "Punched in but no punch-out recorded"],
    ["Absent", "#FEF2F2", "No attendance record on a working day"],
    ["Weekly Off", "#F1F5F9", "Regular day off per working-days policy"],
    ["Company Hol.", "#FEF3C7", "Public / company holiday"],
    ["Leave", "#FFEDD5", "Full-day approved leave"],
    ["½ Day Leave", "#FEF9C3", "Half-day approved leave"],
  ];
  legendData.forEach((row, r) => {
    row.forEach((val, c) => {
      const ref = `${colLetter(c)}${r + 1}`;
      const isBg = c === 1 && r > 0;
      legendWs[ref] = {
        v: isBg ? "" : val,
        t: "s",
        s: makeStyle(
          isBg ? val.replace("#", "") : r === 0 ? "1E3A5F" : "FFFFFF",
          r === 0 ? "FFFFFF" : "374151",
          { bold: r === 0, border: true, center: c !== 2 },
        ),
      };
    });
  });
  legendWs["!cols"] = [{ wch: 16 }, { wch: 14 }, { wch: 40 }];
  legendWs["!ref"] = `A1:C${legendData.length}`;
  XLSX.utils.book_append_sheet(wb, legendWs, "Legend");

  // ── Column widths + row heights for main sheet ─────────────────────────────
  const colWidths = [
    { wch: 24 }, // employee name
    ...Array(STAT_COLS).fill({ wch: 11 }), // stat cols
    ...Array(daysInMonth).fill({ wch: 10 }), // day cols
  ];
  ws["!cols"] = colWidths;

  // row heights: header taller, data rows tall enough for wrapped punch times
  ws["!rows"] = [
    { hpx: 26 }, // title
    { hpx: 36 }, // header (day names wrap)
    ...Array(employeeList.length).fill({ hpx: 52 }),
    { hpx: 26 }, // totals
  ];

  // merge title row across all columns
  const totalCols = 1 + STAT_COLS + daysInMonth;
  ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } }];

  ws["!ref"] = `A1:${colLetter(totalCols - 1)}${TOTAL_ROW + 1}`;
  XLSX.utils.book_append_sheet(wb, ws, monthLabel);

  // ── Write file ─────────────────────────────────────────────────────────────
  XLSX.writeFile(
    wb,
    `attendance_calendar_${monthLabel.replace(/\s/g, "_")}.xlsx`,
  );
}
